// src/utils/toolStorage.js
//
// Per-account local storage for the fourteen tools, and the one seam the hub
// sync reads through. Ported from Friendships Vibe (utils/toolStorage.js)
// with two additions the MindEase tools needed: most of them keep a bare
// ARRAY (a history of check-ins, a list of parked worries), which cannot
// carry a timestamp, so arrays are wrapped; and they autosave from an effect
// on every state change, so an unchanged save is a no-op rather than a
// re-stamp (otherwise opening a tool on a stale device would push that
// stale copy to the hub as the newest).
//
// The tools used to write flat keys - `mindease_vibe_anxiety_thermometer_history`
// and so on - with no account scoping, so on a shared device (a family iPad, a
// school laptop) the next person saw the previous person's panic plan and
// worry list. Keys are now scoped per uid, signing out clears the scope, and
// anything under an old flat key is migrated into the current scope once.
//
// Australian English throughout.

const PREFIX = 'mev:tool';
const ANON = 'anon';

// The flat keys the tools wrote before scoping existed. Also the list of what
// the hub sync carries.
export const LEGACY_TOOL_KEYS = [
  'mindease_vibe_academic_pressure_sessions',
  'mindease_vibe_anxiety_thermometer_history',
  'mindease_vibe_bodyScan_history',
  'mindease_vibe_breathing_history',
  'mindease_vibe_fear_ladders',
  'mindease_vibe_panic_sos_data',
  'mindease_vibe_anxiety_episodes',
  'mindease_vibe_safetyBehaviours_selected',
  'mindease_vibe_safetyBehaviours_custom',
  'mindease_vibe_safetyBehaviours_experiments',
  'mindease_vibe_social_media_sessions',
  'mindease_vibe_text_anxiety_analyses',
  'mindease_vibe_thought_court_cases',
  'mindease_vibe_trigger_mapper',
  'mindease_vibe_selected_values',
  'mindease_vibe_custom_values',
  'mindease_vibe_courage_actions',
  'mindease_vibe_parked_worries',
  'mindease_vibe_archived_worries',
  'mindease_vibe_worry_time',
];

// Tools have no auth context of their own, so the scope is resolved here.
// Signed-out use is kept under `anon` rather than being refused - the tools
// deliberately work without an account.
let authRef = null;
export function registerToolStorageAuth(auth) {
  authRef = auth;
}

const currentUid = () => {
  try {
    return authRef?.currentUser?.uid || null;
  } catch {
    return null;
  }
};

const scopeFor = (uid) => (uid !== undefined && uid !== null ? String(uid) : (currentUid() || ANON));
const scopedKey = (toolKey, uid) => `${PREFIX}:${scopeFor(uid)}:${toolKey}`;

// What sits under a scoped key ("an entry"):
//   a plain object  -> { ...data, __updatedAt }
//   an array        -> { __array: true, items: [...], __updatedAt }
//   anything else   -> stored as is (no stamp possible)
// A legacy flat key holds whatever the tool wrote (usually a bare array); it is
// copied across verbatim and takes its stamp on the next real save.
const isPlainObject = (v) => !!v && typeof v === 'object' && !Array.isArray(v);
const isWrappedArray = (e) => isPlainObject(e) && e.__array === true;

const unwrap = (entry) => {
  if (isWrappedArray(entry)) return Array.isArray(entry.items) ? entry.items : [];
  return entry;
};

// The tool's view of its data, without the stamp.
const bare = (entry) => {
  const data = unwrap(entry);
  if (isPlainObject(data)) {
    const { __updatedAt, ...rest } = data;
    return rest;
  }
  return data;
};

const wrap = (data, stamp) => {
  if (Array.isArray(data)) return { __array: true, items: data, __updatedAt: stamp };
  if (isPlainObject(data)) return { ...data, __updatedAt: stamp };
  return data;
};

const isEmpty = (v) =>
  (Array.isArray(v) && v.length === 0) || (isPlainObject(v) && Object.keys(v).length === 0);

const same = (a, b) => {
  try {
    return JSON.stringify(a) === JSON.stringify(b);
  } catch {
    return false;
  }
};

/**
 * The raw entry (stamp and wrapper included) for this account - what the hub
 * sync compares and carries. Migrates a legacy flat key on first read.
 */
export function loadToolEntry(toolKey, uid) {
  try {
    const key = scopedKey(toolKey, uid);
    const scoped = localStorage.getItem(key);
    if (scoped !== null) return JSON.parse(scoped);

    const legacy = localStorage.getItem(toolKey);
    if (legacy !== null) {
      localStorage.setItem(key, legacy);
      localStorage.removeItem(toolKey);
      return JSON.parse(legacy);
    }
    return null;
  } catch {
    // Private windows, cleared site data, or storage disabled entirely.
    return null;
  }
}

/** A tool's saved data for this account, in the shape the tool wrote it. */
export function loadToolData(toolKey, uid) {
  return bare(loadToolEntry(toolKey, uid));
}

// Two things legitimately want to know that a save happened:
//
//   - the hub sync (ToolSyncService), which marks the tool dirty and pushes
//   - the "Saved" indicator the tools page shows, so a person can see that
//     what they entered actually went somewhere
//
// The sync keeps its single slot; the UI gets a list, because any number of
// listeners may be mounted. Neither import this module's callers, so there is
// no circular import and everything still works if sync is never wired up.
let onSaved = null;
export function registerToolSyncHook(fn) {
  onSaved = typeof fn === 'function' ? fn : null;
}

const saveListeners = new Set();

/**
 * Be told whenever a tool saves. Returns an unsubscribe function.
 *
 * Listeners fire only after the write has succeeded, so an indicator driven
 * by this cannot claim a save that did not happen.
 */
export function subscribeToSaves(fn) {
  if (typeof fn !== 'function') return () => {};
  saveListeners.add(fn);
  return () => saveListeners.delete(fn);
}

const announce = (toolKey) => {
  try { onSaved?.(toolKey); } catch { /* sync is optional */ }
  saveListeners.forEach((fn) => {
    try { fn(toolKey); } catch { /* indicators are cosmetic */ }
  });
};

/**
 * Save a tool's data for this account. Never throws.
 *
 * Stamps __updatedAt so the hub sync can tell which copy is newer without
 * merging two personal journals field by field. A save that changes nothing
 * is a no-op: the tools autosave from an effect that also fires on mount, and
 * re-stamping an unchanged copy would make a stale device's copy "newest".
 */
export function saveToolData(toolKey, data, uid) {
  try {
    const key = scopedKey(toolKey, uid);
    const existing = loadToolEntry(toolKey, uid);
    if (same(bare(existing), data)) return true;
    // Several tools autosave from an effect that also runs on mount, with the
    // empty initial state. Writing that would stamp "[]" as the newest copy
    // on a device that has nothing yet, and flash "Saved" for nothing.
    if (existing === null && isEmpty(data)) return true;
    localStorage.setItem(key, JSON.stringify(wrap(data, Date.now())));
    // Local write already succeeded; sync is best-effort and must never
    // affect whether the user's work was saved.
    announce(toolKey);
    return true;
  } catch {
    return false;
  }
}

/**
 * Store an entry exactly as the hub holds it - the hydrate path. Keeps the
 * remote stamp and does NOT mark the tool dirty: re-stamping a copy pulled
 * down and pushing it straight back would loop, and flashing "Saved" for it
 * would be a lie, since the person did nothing.
 */
export function saveToolEntry(toolKey, entry, uid) {
  try {
    localStorage.setItem(scopedKey(toolKey, uid), JSON.stringify(entry));
    return true;
  } catch {
    return false;
  }
}

/**
 * A tool's "clear my history" action. Writes an EMPTY entry with a fresh
 * stamp rather than deleting the key: a deleted key would be refilled from
 * the hub on the next visit, undoing what the person just did. Empty-and-newer
 * wins on every device.
 */
export function removeToolData(toolKey, uid) {
  try {
    const existing = unwrap(loadToolEntry(toolKey, uid));
    const empty = Array.isArray(existing) ? [] : {};
    localStorage.setItem(scopedKey(toolKey, uid), JSON.stringify(wrap(empty, Date.now())));
    localStorage.removeItem(toolKey);
    announce(toolKey);
    return true;
  } catch {
    return false;
  }
}

/**
 * Remove every tool's data for one account. Called on sign-out and "Clear all
 * data" so a panic plan and a worry list do not sit on a shared device waiting
 * for the next person. Does not tell the sync: the point is that this
 * person's work stops moving from this browser.
 */
export function clearToolData(uid) {
  let removed = 0;
  try {
    for (const toolKey of LEGACY_TOOL_KEYS) {
      const key = scopedKey(toolKey, uid);
      if (localStorage.getItem(key) !== null) {
        localStorage.removeItem(key);
        removed += 1;
      }
      // Sweep any stragglers still on the unscoped key.
      if (localStorage.getItem(toolKey) !== null) {
        localStorage.removeItem(toolKey);
        removed += 1;
      }
    }
  } catch {
    // Nothing to do - storage unavailable.
  }
  return removed;
}

/** True when this device is holding tool data for the given account. */
export function hasToolData(uid) {
  try {
    return LEGACY_TOOL_KEYS.some(
      (toolKey) => localStorage.getItem(scopedKey(toolKey, uid)) !== null,
    );
  } catch {
    return false;
  }
}

/**
 * A localStorage-shaped face on the above, so a tool written against
 * `localStorage.getItem(KEY)` / `setItem(KEY, JSON.stringify(x))` changes one
 * identifier and nothing else. Strings in, strings out; the parsing and the
 * scoping happen here.
 */
export const toolStore = {
  getItem(toolKey) {
    const data = loadToolData(toolKey);
    if (data === null || data === undefined) return null;
    try {
      return JSON.stringify(data);
    } catch {
      return null;
    }
  },
  setItem(toolKey, value) {
    let parsed;
    try {
      parsed = typeof value === 'string' ? JSON.parse(value) : value;
    } catch {
      return false;
    }
    return saveToolData(toolKey, parsed);
  },
  removeItem(toolKey) {
    return removeToolData(toolKey);
  },
};
